<template>
	<div>
		<CDialog
			:visible="show && !!users"
			:title="title"
			custom-class="dialog dialog--medium permission-override-dialog"
			@done="$emit('close')"
			@closed="$emit('closed')"
		>
			<template #title-helptext>
				<span class="helptext">Users that have more than limited access to <i>{{ name }}</i>.</span>
			</template>
			<section class="permissions-user-table">
				<CUserTableBody
					:users="filteredUsers"
					:can-select-current-user="!isFolder"
					is-permission-scope
					@user-selected="handleUserClick"
				>
				</CUserTableBody>

				<CIconButton
					v-if="showAddUserButton"
					class="mt-4"
					@click="openAddUserDialog"
				>
					Add User
				</CIconButton>
			</section>

			<!-- floating permission selector menu -->
			<CPermissionContextMenu
				v-if="selectedUser && showPermissionContextMenu(selectedUser)"
				:is-visible="selectedUser.permissionsOpen"
				:entity-id="entityId"
				:forms-profile-id="selectedUser.id"
				:dialog-title="selectedUser.fullName"
				:assigned-workflow-roles="selectedUser.assignedWorkflowRoles"
				:global-permission="selectedUser.globalPermission"
				:override-permission="selectedUser.overridePermission"
				:folder-permission-override="selectedUser.inheritedPermission"
				:is-nested-form="isNestedForm"
				:type="isFolder ? 'folder' : 'form'"
				:show-permission="showPermissions(selectedUser)"
				:trigger="trigger"
				container-class=".permission-override-dialog .user-table__body"
				class="pr-3 ml-auto"
				@node-change="(event) => handleNodeChanged(event, selectedUser)"
				@permissions-changed="handlePermissionChanged"
				@close="selectedUser.permissionsOpen = false"
			>
				<template #dialogHeaderImage>
					<CAvatar
						:avatar="selectedUser.avatar"
						size="m"
					/>
				</template>
			</CPermissionContextMenu>
		</CDialog>
		<CDialog
			:visible="showAddUserDialog"
			custom-class="add-user-permission-dialog dialog--tiny"
			title="Add User"
			destroy-on-close
			@close="closeAddUserDialog"
		>
			<CAddUserPermissionDialog
				:users="usersWithAvatars"
				:show="showAddUserDialog"
				:form-or-folder-id="entityId"
				:is-nested-form="isNestedForm"
				:type="isFolder ? 'folder' : 'form'"
				:roles="roles"
				:get-name="getName"
				:current-user-email="currentUserEmail"
				@permissions-changed="handlePermissionChanged"
			/>
		</CDialog>
	</div>
</template>

<script lang="ts">
	import { watch, defineComponent, ref, computed, onUnmounted, type PropType } from 'vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CAddUserPermissionDialog from './AddUserPermissionDialog.vue';
	import CUserTableBody from 'src/views/settings/components/users/UserTableBody.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CAvatar from 'src/components/auth/Avatar.vue';
	import CPermissionContextMenu from 'src/components/navigation/context-menu/PermissionContextMenuContainer.vue';
	import type { WorkflowRoleModel } from '@cognitoforms/types/server-types/model/workflow-role-model';
	import type { UserPermissionModel } from '@cognitoforms/types/server-types/model/user-permission-model';
	import type { NodePermissionChangeEvent, NodeTriggerEvent } from 'src/components/navigation/context-menu/PermissionContextMenuContainer.vue';
	import type { UserTableRole, UserTableUser } from 'src/views/settings/components/user-table-user';
	import session from '@cognitoforms/api/services/session';
	import { getAvatar } from '@cognitoforms/utils/avatar';

	export type UserWithAvatar = UserPermissionModel & {
		avatar: string
	}

	export default defineComponent({
		name: 'CPermissionOverrideDialog',
		components: {
			CDialog,
			CIconButton,
			CAddUserPermissionDialog,
			CUserTableBody,
			CAvatar,
			CPermissionContextMenu
		},
		props: {
			show: {
				type: Boolean,
				default: false
			},
			isFolder: {
				type: Boolean,
				default: false
			},
			isNestedForm: {
				type: Boolean,
				default: false
			},
			entityId: {
				type: String,
				default: ''
			},
			name: {
				type: String,
				default: ''
			},
			users: {
				type: Array as PropType<UserPermissionModel[]>,
				default: () => []
			},
			roles: {
				type: Array as PropType<WorkflowRoleModel[]>,
				default: () => []
			}
		},
		emits: ['permissions-changed', 'close', 'closed'],
		setup(props, { emit }) {
			const showAddUserDialog = ref<boolean>(false);
			const currentUserEmail = ref<string>(session.value?.userEmail);

			const title = computed(() => {
				return (props.isFolder ? 'Folder' : 'Form') + ' Permissions';
			});

			const getPermissionDisplayName = (permission: string) => {
				if (permission === 'None')
					return 'Limited Access';
				else if (permission === 'Owner')
					return 'Administrator';
				return permission;
			};

			const getPermission = (user: UserPermissionModel) => {
				const permission = user.PermissionOverride?.DisplayName ?? user.InheritedPermission?.DisplayName ?? user.GlobalPermission.DisplayName;
				return getPermissionDisplayName(permission);
			};

			const getRoles = (assignedWorkflowRoles: WorkflowRoleModel[]): UserTableRole[] => {
				return assignedWorkflowRoles.map(r => ({
					name: r.RoleName,
					isPublic: r.IsPublic,
					isInternal: r.IsInternal
				} as UserTableRole));
			};

			const getName = (user: UserPermissionModel) => {
				if (!user)
					return 'Add User';
				else if (user.FirstName && user.LastName)
					return user.FirstName + ' ' + user.LastName;
				else if (user.FirstName)
					return user.FirstName;
				return user.Email;
			};

			const filteredUsers = ref<UserTableUser[]>([]);
			const usersWithAvatars = ref<UserWithAvatar[]>([]);

			let initialOrder: string[] = null;
			onUnmounted(() => (initialOrder = null));

			watch(() => props.users, () => {
				if (!props.users)
					return;

				const filteredUsersMap: Record<string, UserTableUser> = {};
				usersWithAvatars.value = [];

				const userIdWithOpenPermission = Object.keys(filteredUsers.value).find(key => filteredUsers.value[key].permissionsOpen);

				for (const u of props.users) {
					const avatar = getAvatar(u);
					usersWithAvatars.value.push({ ...u, avatar });

					const userPermission = getPermission(u);
					if (userPermission === 'Limited Access' && (!u.AssignedWorkflowRoles || u.AssignedWorkflowRoles.length < 1))
						continue;

					// Add new FormProfileIds if initialOrder is already defined
					if (initialOrder && !initialOrder.includes(u.FormProfileId))
						initialOrder.push(u.FormProfileId);

					filteredUsersMap[u.FormProfileId] = {
						id: u.FormProfileId,
						avatar,
						canDelete: false,
						isInvitation: false,
						firstName: u.FirstName,
						lastName: u.LastName,
						fullName: getName(u),
						email: u.Email,
						permission: null,
						overridePermission: getPermissionDisplayName(u.PermissionOverride?.DisplayName),
						inheritedPermission: getPermissionDisplayName(u.InheritedPermission?.DisplayName),
						globalPermission: getPermissionDisplayName(u.GlobalPermission.DisplayName),
						// Don't allow user to change their own permission
						canChangePermissions: session.value?.enabledFeatures.IndependentTaskViews ? true : session.value?.userEmail !== u.Email,
						// Keep options open when rebuilding tableUser
						permissionsOpen: userIdWithOpenPermission === u.FormProfileId,
						assignedWorkflowRoles: u.AssignedWorkflowRoles,
						roles: getRoles(u.AssignedWorkflowRoles)
					} as UserTableUser;
				}

				if (initialOrder)
					filteredUsers.value = initialOrder.filter(id => filteredUsersMap[id]).map(id => filteredUsersMap[id]);
				else {
					initialOrder = Object.keys(filteredUsersMap);
					filteredUsers.value = Object.values(filteredUsersMap);
				}
			}, { immediate: true });

			const openAddUserDialog = () => {
				showAddUserDialog.value = true;
			};

			const closeAddUserDialog = () => {
				showAddUserDialog.value = false;
			};

			const showAddUserButton = computed(() => {
				return props.users && props.users.length > 1;
			});

			const showPermissions = (user) => {
				// Don't allow current user to change their own permission only their role
				return user.email !== session.value?.userEmail;
			};

			const showPermissionContextMenu = (user) => {
				// if current user and folder, don't show permission menu
				if (user.email === session.value?.userEmail && props.isFolder)
					return false;
				return user.canChangePermissions || session.value?.enabledFeatures.IndependentTaskViews;
			};

			const handlePermissionChanged = () => {
				closeAddUserDialog();
				emit('permissions-changed');
			};

			const handleNodeChanged = (event: NodePermissionChangeEvent, user: UserTableUser) => {
				user.assignedWorkflowRoles = event.workflowRoles.length ? event.workflowRoles : null;
				user.roles = user.assignedWorkflowRoles ? getRoles(user.assignedWorkflowRoles) : [];
				user.overridePermission = event.permission.includes('Same as') ? null : event.permission;
				user.permission = event.permission.replace(/\([^)]*\)/g, '').trim();
			};

			const selectedUser = ref<UserTableUser>(null);
			const trigger = ref<NodeTriggerEvent | null>(null);

			const handleUserClick = (event): void => {
				// momentarily set to null to force re-render of permission menu
				selectedUser.value = null;
				trigger.value = null;

				selectedUser.value = event[0] as UserTableUser;
				trigger.value = { clientX: event[1].clientX, clientY: event[1].clientY } as NodeTriggerEvent;

				// Close all other open permission menus
				filteredUsers.value.forEach(user => {
					if (user.id !== selectedUser.value.id) {
						user.permissionsOpen = false;
					}
				});

				// Toggle the clicked user's permission menu
				if (selectedUser.value.canChangePermissions) {
					selectedUser.value.permissionsOpen = !selectedUser.value.permissionsOpen;
				}
			};

			return {
				showAddUserDialog,
				showAddUserButton,
				title,
				filteredUsers,
				usersWithAvatars,
				currentUserEmail,
				getName,
				handleUserClick,
				openAddUserDialog,
				closeAddUserDialog,
				handlePermissionChanged,
				handleNodeChanged,
				showPermissions,
				showPermissionContextMenu,
				selectedUser,
				trigger
			};
		}
	});

</script>

<style lang="scss">
.permissions-user-table {

	tr:not(.user-table__row--current-user):hover td {
		background: var(--bg-hovered);
	}

	.user-table {

		&__row--current-user {
			cursor: default;
		}

		&__body {
			min-height: 56px;
			max-height: min(441px, calc(100vh - 220px));
			/* stylelint-disable-next-line declaration-block-no-duplicate-properties */
			max-height: min(441px, calc(100dvh - 220px));

			@include scrollbar;
		}
	}
}
</style>